import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDate, formatRelativeTime } from '@tabita/i18n';
import { useAuth } from '@/auth/auth-context';
import { useI18n } from '@/i18n/i18n-provider';
import { authApi } from '@/services/auth.api';
import { billingApi } from '@/services/billing.api';
import { peopleApi } from '@/services/people.api';
import { groupsApi } from '@/services/groups.api';
import { eventsApi } from '@/services/events.api';
import { notificationsApi } from '@/services/notifications.api';
import { ApiError } from '@/services/api-client';
import { Alert } from '@/components/ui';
import { MODULE_TILES, MODULE_CATEGORY_LABEL_KEYS, type ModuleCategory } from './module-tiles';
import { MODULE_ICONS } from './module-icons';
import { ShortcutsWidget } from './shortcuts-widget';
import { BirthdaysWidget } from './birthdays-widget';
import { InventoryAlertsWidget } from './inventory-alerts-widget';

const CATEGORIES: readonly ModuleCategory[] = ['community', 'communications', 'finance', 'system'];

/**
 * The post-login home screen: a greeting, a few real numbers (never fabricated
 * — see the counts below), and every module the caller can actually open,
 * grouped the way `navigation.*`'s category keys already anticipated.
 *
 * `people`/`groups` show "N" or "N+" — the API's cursor pagination has no
 * total, so `N+` (mirrors `people-list-page.tsx`'s own header count) is the
 * honest floor rather than a fabricated exact total.
 */
export function DashboardPage() {
  const { t, locale } = useI18n();
  const { can } = useAuth();

  const session = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: ({ signal }) => authApi.me(signal),
  });
  const subscription = useQuery({
    queryKey: ['billing', 'subscription'],
    queryFn: () => billingApi.getSubscription(),
    enabled: can('billing.subscription.read'),
  });
  const people = useQuery({
    queryKey: ['dashboard', 'people'],
    queryFn: ({ signal }) => peopleApi.list({ limit: 100 }, signal),
    enabled: can('people.person.read'),
  });
  const groups = useQuery({
    queryKey: ['dashboard', 'groups'],
    queryFn: ({ signal }) => groupsApi.list({ limit: 100 }, signal),
    enabled: can('groups.group.read'),
  });
  const events = useQuery({
    queryKey: ['dashboard', 'events'],
    queryFn: ({ signal }) => eventsApi.list({ limit: 50 }, signal),
    enabled: can('events.event.read'),
  });
  const notifications = useQuery({
    queryKey: ['dashboard', 'notifications'],
    queryFn: ({ signal }) => notificationsApi.list({ limit: 25 }, signal),
    enabled: can('notification.log.read'),
  });

  const visibleTiles = MODULE_TILES.filter((tile) => can(tile.permission));
  const tilesByCategory = CATEGORIES.map((category) => ({
    category,
    tiles: visibleTiles.filter((tile) => tile.category === category),
  })).filter((group) => group.tiles.length > 0);

  const nextEvent =
    events.data?.data
      .filter(
        (event) =>
          event.status !== 'CANCELLED' && event.status !== 'COMPLETED' && !event.archivedAt,
      )
      .filter((event) => new Date(event.startAt).getTime() >= Date.now())
      .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime())[0] ?? null;

  const isTrialing = subscription.data?.status === 'TRIALING' && subscription.data.trialEndsAt;

  return (
    <div className="page dashboard">
      {isTrialing && subscription.data?.trialEndsAt && (
        <Alert tone="warning">
          <div className="dashboard-banner-row">
            <span>
              {t('dashboard.trialBanner', {
                values: {
                  relative: formatRelativeTime(subscription.data.trialEndsAt, locale),
                },
              })}
            </span>
            {can('billing.subscription.update') && (
              <Link to="/billing" className="dashboard-banner-cta">
                {t('dashboard.manageModules')}
              </Link>
            )}
          </div>
        </Alert>
      )}

      {session.isError && (
        <Alert tone="danger">
          {session.error instanceof ApiError
            ? t(`errors.${session.error.code}`, { fallback: t('errors.INTERNAL_ERROR') })
            : t('errors.INTERNAL_ERROR')}
        </Alert>
      )}

      <header className="dashboard-greeting">
        <h1 className="page-title">
          {t('dashboard.greeting', {
            values: { name: session.data?.user.displayName ?? session.data?.user.email ?? '' },
          })}
        </h1>
        {session.data?.tenant && <p className="page-count">{session.data.tenant.name}</p>}
      </header>

      {(can('people.person.read') ||
        can('groups.group.read') ||
        can('events.event.read') ||
        can('notification.log.read')) && (
        <div className="dashboard-stats">
          {can('people.person.read') && (
            <div className="dashboard-stat">
              <span className="dashboard-stat-label">{t('people.person.title')}</span>
              <span className="dashboard-stat-value">
                {people.data
                  ? `${t('people.person.count', { count: people.data.data.length })}${
                      people.data.pagination.hasMore ? '+' : ''
                    }`
                  : '—'}
              </span>
            </div>
          )}

          {can('groups.group.read') && (
            <div className="dashboard-stat">
              <span className="dashboard-stat-label">{t('groups.group.title')}</span>
              <span className="dashboard-stat-value">
                {groups.data
                  ? `${t('groups.group.count', { count: groups.data.data.length })}${
                      groups.data.pagination.hasMore ? '+' : ''
                    }`
                  : '—'}
              </span>
            </div>
          )}

          {can('events.event.read') && (
            <div className="dashboard-stat">
              <span className="dashboard-stat-label">{t('dashboard.nextEvent')}</span>
              {!events.data ? (
                <span className="dashboard-stat-value">—</span>
              ) : nextEvent ? (
                <>
                  <span className="dashboard-stat-value dashboard-stat-value-text">
                    {nextEvent.name}
                  </span>
                  <span className="dashboard-stat-hint" dir="ltr">
                    {formatDate(nextEvent.startAt, locale)}
                  </span>
                </>
              ) : (
                <span className="dashboard-stat-value dashboard-stat-value-text">
                  {t('dashboard.noUpcomingEvents')}
                </span>
              )}
            </div>
          )}

          {can('notification.log.read') && (
            <div className="dashboard-stat">
              <span className="dashboard-stat-label">{t('dashboard.recentNotifications')}</span>
              <span className="dashboard-stat-value">
                {notifications.data ? notifications.data.data.length : '—'}
              </span>
            </div>
          )}
        </div>
      )}

      <BirthdaysWidget />

      <InventoryAlertsWidget />

      <ShortcutsWidget />

      {tilesByCategory.length > 0 && (
        <>
          <h2 className="dashboard-section-title">{t('dashboard.yourModules')}</h2>

          {tilesByCategory.map(({ category, tiles }) => (
            <div className="dashboard-category" key={category}>
              <p className="dashboard-category-label">{t(MODULE_CATEGORY_LABEL_KEYS[category])}</p>
              <div className="dashboard-tiles">
                {tiles.map((tile) => {
                  const Icon = MODULE_ICONS[tile.to];
                  return (
                    <Link to={tile.to} className="dashboard-tile" key={tile.to}>
                      <span className="dashboard-tile-icon">{Icon && <Icon />}</span>
                      <span className="dashboard-tile-label">{t(tile.labelKey)}</span>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </>
      )}
    </div>
  );
}
